import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { numberToCurrency } from '@/shared/helpers/String'
import { setQuantity, selectQuantity } from '@/public/redux/productSlice'
import QuantityInput from '@/public/product/QuantityInput'
import AddToCartButton from '@/public/product/AddToCartButton'
import PolicyLinks from '@/public/product/PolicyLinks'
import PropTypes from 'prop-types'
import UseCart from '@/public/hooks/use-cart'
import useInvoicedSupplement from '@/public/hooks/use-invoiced-supplement'
import getContractPricing from '@/public/helpers/get-contract-pricing'
import { MEMBERSHIP_DISCOUNT_MULTIPLIER, MEMBERSHIP_DISCOUNT_CATEGORIES } from '@/shared/helpers/pricing'
import PriceBlockMembershipOptions from './PriceBlockMembershipOptions'
import NotifyWhenAvailable from './NotifyWhenAvailable'

export default function PriceBlockWithQuantity({
  product,
  returnPolicy,
  shippingPolicy,
  customerIsMember,
  awaitedProductIds,
  accessMembershipPrice,
  annualAccessMembershipPrice,
  showSubscribeButton = true,
  defaultMembershipOption = null,
  membershipPlans = [],
  membershipPlansEnabled = false,
  contractPricingData = null,
}) {
  const dispatch = useDispatch()
  const quantity = useSelector(selectQuantity)
  const backOrdered = /back_ordered/i.test(product.status)

  const { cart } = UseCart()
  const cartContainsMembership = cart?.contents?.some((item) => item.data.category === 'membership')
  // Behind the flag the shopper picks a plan, so the selection IS a SKU and the
  // cheapest plan is pre-selected -- it is the one the cart would auto-add
  // anyway, so the page and the cart agree before the shopper touches anything.
  const hasPlans = membershipPlans.length > 0
  const [selectedOption, setSelectedOption] = useState(
    hasPlans ? membershipPlans[0].sku : defaultMembershipOption,
  )
  // A non-member buying a lab or test needs a membership; the picker is how they
  // choose one, and it is hidden once the cart already holds one.
  const membershipRequired = (product.category === 'lab' || product.category === 'test') && !customerIsMember
  // Behind the flag only a plan ON SALE counts as a membership the shopper has
  // already chosen -- the same rule the endpoints apply. A pre-rollout ACCESS
  // line would otherwise hide the picker while the server rejects it as a
  // choice, so adding a lab evicts it and auto-adds the cheapest plan and the
  // shopper never gets to pick among the plans this rollout exists to offer.
  const cartHoldsPlanOnSale = membershipPlans.some(
    (plan) => (cart?.contents || []).some((line) => line.data?.sku === plan.sku),
  )
  const cartMembershipCounts = membershipPlansEnabled ? cartHoldsPlanOnSale : cartContainsMembership
  const membershipPickerShown = membershipRequired && !cartMembershipCounts
  // The outgoing SKU is gated on the picker rather than on the plan list alone:
  // every price block receives the list, so a supplement page would otherwise
  // post the cheapest plan's SKU and have a membership added alongside a
  // product that never asked for one, with no control on screen the shopper
  // could have used to decline it.
  const membershipSku = hasPlans && membershipPickerShown ? selectedOption : ''
  // Flag on with nothing on sale: the picker says the order cannot be placed
  // yet, and this is what makes that true. Without it the shopper could add the
  // lab anyway and reach checkout with no membership behind it -- the one thing
  // the membership gate on labs exists to prevent.
  //
  // Keyed on membershipRequired, NOT on the picker being visible. A cart already
  // holding a membership hides the picker, but if that membership is stale the
  // refresh evicts it and finds no plan to auto-add -- so the shopper would be
  // left with exactly the lab-without-a-membership this guards against, and no
  // control on screen would have warned them.
  const membershipUnavailable = membershipRequired && membershipPlansEnabled && !hasPlans
  // Left exactly as it was. The legacy param is posted from every price block
  // today, including ones that render no picker, and narrowing it here would
  // change flag-off behaviour -- see the PR description.
  const membershipOption = hasPlans ? '' : selectedOption
  const isInvoicedSupplement = useInvoicedSupplement(product, cart)
  const contractPricing = getContractPricing(product, contractPricingData)
  const hasMemberDiscount = customerIsMember
    && MEMBERSHIP_DISCOUNT_CATEGORIES.includes(product.category)

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value)
  }

  const renderPriceDisplay = () => {
    if (isInvoicedSupplement) {
      return (
        <>
          <span>$0.00</span>
          <span className="fw-light fs-6 d-block text-success">
            Covered by employer
          </span>
        </>
      )
    }

    if (contractPricing.isDiscounted && hasMemberDiscount) {
      const memberPrice = contractPricing.discountedPrice * MEMBERSHIP_DISCOUNT_MULTIPLIER
      return (
        <>
          <span>{numberToCurrency(memberPrice)}</span>
          <del className="text-muted ms-2 fw-normal fs-6">
            {numberToCurrency(product.price)}
          </del>
          <span className="fw-light fs-6 d-block text-success">
            {contractPricing.discountPercentage
              ? `${contractPricing.discountPercentage}% employer discount + member price`
              : 'Employer discount + member price'}
          </span>
        </>
      )
    }

    if (contractPricing.isDiscounted) {
      return (
        <>
          <span>{numberToCurrency(contractPricing.discountedPrice)}</span>
          <del className="text-muted ms-2 fw-normal fs-6">
            {numberToCurrency(product.price)}
          </del>
          <span className="fw-light fs-6 d-block text-success">
            {contractPricing.discountPercentage
              ? `${contractPricing.discountPercentage}% employer discount`
              : 'Employer discount'}
          </span>
          {(product.category === 'lab' || product.category === 'test') && !customerIsMember && (
            <span className="fw-light fs-6 d-block d-sm-inline">
              <span className="text-secondary"> + </span>
              Membership
            </span>
          )}
        </>
      )
    }

    if (hasMemberDiscount) {
      const memberPrice = parseFloat(product.price) * MEMBERSHIP_DISCOUNT_MULTIPLIER
      return (
        <>
          <span>{numberToCurrency(memberPrice)}</span>
          <del className="text-muted ms-2 fw-normal fs-6">
            {numberToCurrency(product.price)}
          </del>
          <span className="fw-light fs-6 d-block text-success">
            member price
          </span>
        </>
      )
    }

    return (
      <>
        <span>{numberToCurrency(product.price)}</span>
        {(product.category === 'lab' || product.category === 'test') && !customerIsMember && (
          <span className="fw-light fs-6 d-block d-sm-inline">
            <span className="text-secondary"> + </span>
            Membership
          </span>
        )}
      </>
    )
  }

  return (
    <div className="card">
      <div className="card-content pt-4">
        {
          !backOrdered && (
            <>
              <p className="subtitle secondary-d50">Quantity</p>

              <QuantityInput
                quantity={quantity}
                handleChange={(newQuantity) => dispatch(setQuantity(newQuantity))}
              />

              <hr />
            </>
          )
        }

        <div className="subtitle bold d-flex flex-column flex-sm-row">
          {
            backOrdered ? (
              <>
                <del>{numberToCurrency(product.price)}</del>
                <span className="text-opacity-50 ms-3 text-secondary small fw-normal">Sold Out</span>
              </>
            ) : (
              <>
                <div>
                  {renderPriceDisplay()}
                </div>
                {(product.category === 'lab' || product.category === 'test') && !customerIsMember && (
                  <div className="ms-0 ms-sm-auto">
                    <button
                      type="button"
                      className="btn btn-link d-inline-block align-items-baseline"
                      id="access-membership-modal-button"
                      data-bs-toggle="modal"
                      data-bs-target="#access-membership-modal"
                    >
                      Why do I need a membership?
                    </button>
                  </div>
                )}
              </>
            )
          }
        </div>

        <div className="actions mt-0">
          {
            backOrdered ? (
              <NotifyWhenAvailable product={product} awaitedProductIds={awaitedProductIds} wide />
            ) : (
              <>
                <hr className="mb-2" />

                {membershipPickerShown && (
                  <PriceBlockMembershipOptions
                    selectedOption={selectedOption}
                    handleOptionChange={handleOptionChange}
                    membershipPlans={membershipPlans}
                    membershipPlansEnabled={membershipPlansEnabled}
                    accessMembershipPrice={accessMembershipPrice}
                    annualAccessMembershipPrice={annualAccessMembershipPrice}
                    product={product}
                  />
                )}

                <AddToCartButton
                  product={product}
                  quantity={quantity}
                  className="btn btn-primary w-100"
                  membershipOption={membershipOption}
                  membershipSku={membershipSku}
                  disabled={membershipUnavailable}
                />

                {product.is_subscribable && customerIsMember
                  && (
                    <AddToCartButton
                      product={product}
                      className="btn btn-outline-primary w-100 mt-3"
                      subscription
                      membershipOption={membershipOption}
                      membershipSku={membershipSku}
                      disabled={!showSubscribeButton || membershipUnavailable}
                    >
                      Subscribe
                    </AddToCartButton>
                  )}

                {product.is_subscribable && !customerIsMember
                  && (
                    <AddToCartButton
                      product={product}
                      className="btn btn-outline-primary w-100 mt-3"
                      subscription
                      membershipOption={membershipOption}
                      membershipSku={membershipSku}
                      disabled={!showSubscribeButton || membershipUnavailable}
                    >
                      Subscribe & Save
                      {' '}
                      {product.calculated_subscription_savings_percentage}
                    </AddToCartButton>
                  )}
              </>
            )
          }

          <PolicyLinks
            product={product}
            shippingPolicy={shippingPolicy}
            returnPolicy={returnPolicy}
          />
        </div>
      </div>
    </div>
  )
}

PriceBlockWithQuantity.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    price: PropTypes.string,
    category: PropTypes.string,
    is_subscribable: PropTypes.bool,
    calculated_subscription_savings_percentage: PropTypes.string,
  }).isRequired,
  returnPolicy: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
  }),
  shippingPolicy: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
  }),
  customerIsMember: PropTypes.bool,
  awaitedProductIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  accessMembershipPrice: PropTypes.string,
  annualAccessMembershipPrice: PropTypes.string,
  defaultMembershipOption: PropTypes.string,
  membershipPlans: PropTypes.arrayOf(PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  membershipPlansEnabled: PropTypes.bool,
  contractPricingData: PropTypes.shape({
    discounts: PropTypes.objectOf(
      PropTypes.shape({
        amount: PropTypes.number,
        type: PropTypes.string,
      }),
    ),
    category_map: PropTypes.objectOf(PropTypes.string),
  }),
}
PriceBlockWithQuantity.defaultProps = {
  returnPolicy: null,
  shippingPolicy: null,
  customerIsMember: null,
  accessMembershipPrice: null,
  annualAccessMembershipPrice: null,
  defaultMembershipOption: null,
  membershipPlans: [],
  membershipPlansEnabled: false,
  contractPricingData: null,
}
